import {
  Country,
  ReferrerInfo,
  OAuthProvider,
  OAuthResponse,
  FileInfo,
  FiatCurrency,
} from "@nestcoinco/hyper-api-gateway-api-client";

import Logger from "./logger";
import {
  AuthFlow,
  OnboardingSteps,
  ROUTES,
  TopupOrigins,
  CHALLENGE_TIMER_RESETS_IN,
} from "./constants";
import { STORED_ATOM_KEYS } from "../jotai/atoms/constants";

const SELECTED_COUNTRY_KEY = "hyper_selected_country";
const SELECTED_EARNING_KEY = "hyper_earning_goal";
const SELECTED_CURRENCY_KEY = "hyper_selected_currency";
const SELECTED_STREAK_KEY = "hyper_earning_streak";
const REFERRER_INFO_KEY = "hyper_referrer_info";
const AUTH_PROVIDER_KEY = "hyper_auth_provider";
const AUTH_CANCELLED_ERROR_KEY = "hyper_auth_cancelled";
const APK_KEY = "hyper_apk";
const PAYMENT_RETRIES = "hyper_payment_retries";
const HAS_VIEWED_DASHBOARD_EXPLAINER = "hyper_has_viewed_db_explainer";
const AUTH_FLOW = "hyper_auth_flow";
const HAS_VIEWED_FIRST_GAME_EXPLAINER = "hyper_has_viewed_first_game_explainer";
const PWA_INSTALL_STATUS = "hyper_pwa_install_status";
const NOTIFICATION_PROMPT_TIME = "hyper_notification_prompt_time";
const PWA_INSTALL_ORIGIN = "hyper_pwa_install_origin";
const HAS_SHOWN_AGE_RESTRICTION_NOTICE =
  "hyper_has_shown_age_restriction_notice";
const HAS_SHOWN_NOTIFICATION_PROMPT = "hyper_has_shown_notification_prompt";
const TOPUP_ORIGIN = "hyper_topup_origin";
const HAS_SHOWN_SWIPE_FOR_MORE = "hyper_has_shown_swipe_for_more";
const LAST_CHALLENGE_FRIENDS_MODAL_VIEW =
  "hyper_last_challenge_friends_modal_view";
const CHALLENGE_TIMERS = "hyper_challenge_timers";
const ATTRIBUTION_STORAGE_KEY = "hyper_attribution";
const PHOENIX_CLICK_STORAGE_KEY = "hyper_phoenix_click_id";
const SHOW_GET_APP_AFTER_INSURANCE_CLAIM =
  "hyper_show_get_app_after_insurance_claim";
const POST_ONBOARDING_REDIRECT_KEY = "hyper_post_onboarding_redirect";

function setStoredData(key: string, data: any, isSessionStorage = false) {
  try {
    const storage = isSessionStorage ? sessionStorage : localStorage;
    storage.setItem(key, JSON.stringify(data));
  } catch (error) {
    Logger.error(error);
  }
}

function getStoredData<T>(key: string, isSessionStorage = false): T | null {
  try {
    const storage = isSessionStorage ? sessionStorage : localStorage;
    const storedData = storage.getItem(key);
    return storedData ? JSON.parse(storedData) : null;
  } catch (error) {
    Logger.error(error);
    return null;
  }
}

function deleteStoredData(key: string, isSessionStorage = false) {
  try {
    const storage = isSessionStorage ? sessionStorage : localStorage;
    const storedData = storage.getItem(key);
    if (!storedData) return;
    storage.removeItem(key);
  } catch (error) {
    Logger.error(error);
  }
}

export function setStoredSelectedCountry(country: Country) {
  sessionStorage.setItem(SELECTED_COUNTRY_KEY, JSON.stringify(country));
}

export function getStoredSelectedCountry(): Country | null {
  const storedCountry = sessionStorage.getItem(SELECTED_COUNTRY_KEY);
  return storedCountry ? JSON.parse(storedCountry) : null;
}

export function setStoredSelectedEarnings(hyperAmount: number) {
  sessionStorage.setItem(SELECTED_EARNING_KEY, JSON.stringify(hyperAmount));
}

export function getStoredSelectedEarning(): number | null {
  const storedEarning = sessionStorage.getItem(SELECTED_EARNING_KEY);
  return storedEarning ? JSON.parse(storedEarning) : null;
}

export function setStoredSelectedCurrency(currency: FiatCurrency) {
  sessionStorage.setItem(SELECTED_CURRENCY_KEY, JSON.stringify(currency));
}

export function getStoredSelectedCurrency(): FiatCurrency | null {
  const storedCurrency = sessionStorage.getItem(SELECTED_CURRENCY_KEY);
  return storedCurrency ? JSON.parse(storedCurrency) : null;
}

export function setStoredSelectedStreak(days: number) {
  sessionStorage.setItem(SELECTED_STREAK_KEY, JSON.stringify(days));
}

export function getStoredSelectedStreak(): number | null {
  const storedStreak = sessionStorage.getItem(SELECTED_STREAK_KEY);
  return storedStreak ? JSON.parse(storedStreak) : null;
}

export function setStoredReferrerInfo(referrerInfo: ReferrerInfo) {
  localStorage.setItem(REFERRER_INFO_KEY, JSON.stringify(referrerInfo));
}

export function getStoredReferrerInfo(): ReferrerInfo | null {
  const storedReferrerInfo = localStorage.getItem(REFERRER_INFO_KEY);
  return storedReferrerInfo ? JSON.parse(storedReferrerInfo) : null;
}

export function clearStoredReferrerInfo() {
  localStorage.removeItem(REFERRER_INFO_KEY);
}

export function getStoredUser(): OAuthResponse | null {
  return getStoredData<OAuthResponse>(STORED_ATOM_KEYS.USER);
}

export function setStoredAuthProvider(provider: OAuthProvider) {
  localStorage.setItem(AUTH_PROVIDER_KEY, JSON.stringify(provider));
}

export function getStoredAuthProvider(): OAuthProvider | null {
  const storedAuthProvider = localStorage.getItem(AUTH_PROVIDER_KEY);
  return storedAuthProvider ? JSON.parse(storedAuthProvider) : null;
}

interface AuthCancelledError {
  error: string;
  provider: string;
}
export function setStoredAuthCancelledError(error: AuthCancelledError) {
  sessionStorage.setItem(AUTH_CANCELLED_ERROR_KEY, JSON.stringify(error));
}

export function getStoredAuthCancelledError(): AuthCancelledError | null {
  const error = sessionStorage.getItem(AUTH_CANCELLED_ERROR_KEY);
  return error ? JSON.parse(error) : null;
}

export function setStoredApk(apk: FileInfo) {
  sessionStorage.setItem(APK_KEY, JSON.stringify(apk));
}

export function getStoredApk(): FileInfo | null {
  const storedApk = sessionStorage.getItem(APK_KEY);
  return storedApk ? JSON.parse(storedApk) : null;
}

export function clearStoredAuthCancelledError() {
  sessionStorage.removeItem(AUTH_CANCELLED_ERROR_KEY);
}

interface StoredPaymentRetry {
  retryCount: number;
  lastUpdatedAt: number;
}

export type StoredPaymentRetries = Record<string, StoredPaymentRetry>;

export function setStoredPaymentRetries(retries: StoredPaymentRetries) {
  setStoredData(PAYMENT_RETRIES, retries);
}

export function getStoredPaymentRetries(): StoredPaymentRetries | null {
  const paymentRetries = getStoredData<StoredPaymentRetries>(PAYMENT_RETRIES);
  return paymentRetries;
}

export function clearStoredPaymentRetries() {
  return deleteStoredData(PAYMENT_RETRIES);
}

export function setStoredHasViewedDashboardExplainer(hasViewed: boolean) {
  setStoredData(HAS_VIEWED_DASHBOARD_EXPLAINER, hasViewed);
}

export function getStoredHasViewedDashboardExplainer(): boolean | null {
  return getStoredData<boolean>(HAS_VIEWED_DASHBOARD_EXPLAINER);
}

interface PwaInstallStatusData {
  openedPwa: boolean;
  installationDate: number;
}

export function setStoredPwaInstallStatus(
  pwaInstallStatusData: PwaInstallStatusData
) {
  setStoredData(PWA_INSTALL_STATUS, pwaInstallStatusData);
}

export function getStoredPwaInstallStatus(): PwaInstallStatusData | null {
  return getStoredData<PwaInstallStatusData>(PWA_INSTALL_STATUS);
}

export function deleteStoredPwaInstallStatus() {
  deleteStoredData(PWA_INSTALL_STATUS);
}

export function setStoredNotificationPromptTime(timeInMs: number | null) {
  setStoredData(NOTIFICATION_PROMPT_TIME, timeInMs);
}

export function getStoredNotificationPromptTime(): number | null {
  return getStoredData<number>(NOTIFICATION_PROMPT_TIME);
}

export function setStoredPwaInstallOrigin(origin: string) {
  setStoredData(PWA_INSTALL_ORIGIN, origin);
}

export function getStoredPwaInstallOrigin(): string | null {
  return getStoredData<string>(PWA_INSTALL_ORIGIN);
}

export function setStoredHasViewedFirstGameExplainer(hasViewed: boolean) {
  setStoredData(HAS_VIEWED_FIRST_GAME_EXPLAINER, hasViewed);
}

export function getStoredHasViewedFirstGameExplainer(): boolean | null {
  return getStoredData<boolean>(HAS_VIEWED_FIRST_GAME_EXPLAINER);
}

export function setStoredAuthFlow(flow: AuthFlow) {
  setStoredData(AUTH_FLOW, flow, true);
}

export function getStoredAuthFlow(): AuthFlow | null {
  return getStoredData<AuthFlow>(AUTH_FLOW, true);
}

export function setStoredHasShownAgeRestrictionNotice(status: boolean) {
  setStoredData(HAS_SHOWN_AGE_RESTRICTION_NOTICE, status);
}

export function getStoredHasShownAgeRestrictionNotice(): boolean | null {
  return getStoredData<boolean>(HAS_SHOWN_AGE_RESTRICTION_NOTICE);
}

export function setStoredHasShownNotificationPrompt(status: boolean) {
  setStoredData(HAS_SHOWN_NOTIFICATION_PROMPT, status, true);
}

export function getStoredHasShownNotificationPrompt(): boolean | null {
  return getStoredData(HAS_SHOWN_NOTIFICATION_PROMPT, true);
}

export function setStoredTopupOrigin(origin: TopupOrigins) {
  setStoredData(TOPUP_ORIGIN, origin);
}

export function getStoredTopupOrigin(): TopupOrigins | null {
  return getStoredData(TOPUP_ORIGIN);
}

export function deleteStoredTopupOrigin() {
  return deleteStoredData(TOPUP_ORIGIN);
}

export function setStoredHasShownSwipeForMore(indicatorHasShown: boolean) {
  setStoredData(HAS_SHOWN_SWIPE_FOR_MORE, indicatorHasShown);
}

export function getStoredHasShownSwipeForMore(): boolean | null {
  return getStoredData(HAS_SHOWN_SWIPE_FOR_MORE);
}

export function setStoredLastViewedChallengeFriendsModalTime(
  lastViewedTime: number
) {
  setStoredData(LAST_CHALLENGE_FRIENDS_MODAL_VIEW, lastViewedTime);
}

export function getStoredLastViewedChallengeFriendsModalTime(): number | null {
  return getStoredData(LAST_CHALLENGE_FRIENDS_MODAL_VIEW);
}

export function getStoredChallengeTimers(): Record<string, number> | null {
  return getStoredData<Record<string, number>>(CHALLENGE_TIMERS);
}

export function setStoredChallengeTimer(challengeId: string) {
  const currentStoredTimers = getStoredChallengeTimers();
  let timers: Record<string, number>;
  const expirationTime = Date.now() + CHALLENGE_TIMER_RESETS_IN;
  if (!currentStoredTimers) {
    timers = {
      [challengeId]: expirationTime,
    };
    setStoredData(CHALLENGE_TIMERS, timers);
    return;
  }
  timers = {
    ...currentStoredTimers,
    [challengeId]: expirationTime,
  };
  setStoredData(CHALLENGE_TIMERS, timers);
}

export function setStoredAttribution<T>(data: T) {
  setStoredData(ATTRIBUTION_STORAGE_KEY, data);
}

export function getStoredAttribution<T>(): T | null {
  return getStoredData<T>(ATTRIBUTION_STORAGE_KEY);
}

export function deleteStoredAttribution() {
  deleteStoredData(ATTRIBUTION_STORAGE_KEY);
}

export function setStoredPhoenixClick<T>(data: T) {
  setStoredData(PHOENIX_CLICK_STORAGE_KEY, data);
}

export function getStoredPhoenixClick<T>(): T | null {
  return getStoredData<T>(PHOENIX_CLICK_STORAGE_KEY);
}

export function deleteStoredPhoenixClick() {
  deleteStoredData(PHOENIX_CLICK_STORAGE_KEY);
}

export function setStoredShowGetAppAfterInsuranceClaim(show: boolean) {
  setStoredData(SHOW_GET_APP_AFTER_INSURANCE_CLAIM, show);
}

export function getStoredShowGetAppAfterInsuranceClaim(): boolean {
  return Boolean(getStoredData<boolean>(SHOW_GET_APP_AFTER_INSURANCE_CLAIM));
}

export function deleteStoredShowGetAppAfterInsuranceClaim() {
  deleteStoredData(SHOW_GET_APP_AFTER_INSURANCE_CLAIM);
}

export function getInsuranceBonusFollowUp() {
  if (!getStoredShowGetAppAfterInsuranceClaim()) return null;

  return {
    step: OnboardingSteps.GET_APP_AFTER_BONUS,
    path: `${ROUTES.ROOT.SIGN_UP}/${ROUTES.SIGN_UP.WELCOME}`,
  };
}

export function setStoredPostOnboardingRedirect(path: string | null) {
  setStoredData(POST_ONBOARDING_REDIRECT_KEY, path, true);
}

export function getStoredPostOnboardingRedirect(): string | null {
  return getStoredData<string>(POST_ONBOARDING_REDIRECT_KEY, true);
}

export function clearStoredPostOnboardingRedirect() {
  deleteStoredData(POST_ONBOARDING_REDIRECT_KEY, true);
}

export function consumeStoredPostOnboardingRedirect(
  fallbackPath = ROUTES.ROOT.DASHBOARD
): string {
  const path = getStoredPostOnboardingRedirect();
  clearStoredPostOnboardingRedirect();
  return path || fallbackPath;
}

export function clearSessionStorage() {
  sessionStorage.clear();
}

export function clearLocalStorage() {
  localStorage.clear();
}

export function clearStorage() {
  clearLocalStorage();
  clearSessionStorage();
}
